import React, { useEffect, useRef } from 'react';

interface PipesProps {
  onActivity: () => void;
}

interface Pipe {
  x: number;
  y: number;
  direction: number; // 0: right, 1: down, 2: left, 3: up
  segments: { x: number; y: number; direction: number }[];
  color: string;
  growing: boolean;
  turnChance: number;
}

export const Pipes: React.FC<PipesProps> = ({ onActivity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const pipesRef = useRef<Pipe[]>([]);
  const gridSize = 20;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const colors = [
      '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', 
      '#ffeaa7', '#dda0dd', '#98d8c8', '#fd79a8'
    ];

    const initPipes = () => {
      pipesRef.current = [];
      const rows = Math.floor(canvas.height / gridSize);
      const cols = Math.floor(canvas.width / gridSize);
      
      for (let i = 0; i < 6; i++) {
        pipesRef.current.push({
          x: Math.floor(Math.random() * cols) * gridSize,
          y: Math.floor(Math.random() * rows) * gridSize,
          direction: Math.floor(Math.random() * 4),
          segments: [],
          color: colors[Math.floor(Math.random() * colors.length)],
          growing: true,
          turnChance: 0.1 + Math.random() * 0.1
        });
      }
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initPipes();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const directions = [
      { dx: 1, dy: 0 },  // right
      { dx: 0, dy: 1 },  // down
      { dx: -1, dy: 0 }, // left
      { dx: 0, dy: -1 }  // up
    ];

    let frameCount = 0;

    const animate = () => {
      frameCount++;
      
      // Clear canvas with dark background
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update pipes every few frames for smoother animation
      if (frameCount % 8 === 0) {
        pipesRef.current.forEach(pipe => {
          if (!pipe.growing) return;

          // Add current position to segments
          pipe.segments.push({
            x: pipe.x,
            y: pipe.y,
            direction: pipe.direction
          });

          // Limit segment length
          if (pipe.segments.length > 50) {
            pipe.segments.shift();
          }

          // Move pipe
          const dir = directions[pipe.direction];
          pipe.x += dir.dx * gridSize;
          pipe.y += dir.dy * gridSize;

          // Check boundaries and turn or stop
          if (pipe.x < 0 || pipe.x >= canvas.width || 
              pipe.y < 0 || pipe.y >= canvas.height) {
            pipe.growing = false;
            return;
          }

          // Random turn chance
          if (Math.random() < pipe.turnChance) {
            const possibleTurns = [];
            for (let i = 0; i < 4; i++) {
              if (i !== pipe.direction && i !== (pipe.direction + 2) % 4) {
                const testDir = directions[i];
                const newX = pipe.x + testDir.dx * gridSize;
                const newY = pipe.y + testDir.dy * gridSize;
                if (newX >= 0 && newX < canvas.width && 
                    newY >= 0 && newY < canvas.height) {
                  possibleTurns.push(i);
                }
              }
            }
            if (possibleTurns.length > 0) {
              pipe.direction = possibleTurns[Math.floor(Math.random() * possibleTurns.length)];
            }
          }
        });

        // Remove completed pipes and add new ones
        pipesRef.current = pipesRef.current.filter(pipe => pipe.growing || pipe.segments.length > 0);
        
        if (pipesRef.current.length < 6) {
          const rows = Math.floor(canvas.height / gridSize);
          const cols = Math.floor(canvas.width / gridSize);
          pipesRef.current.push({
            x: Math.floor(Math.random() * cols) * gridSize,
            y: Math.floor(Math.random() * rows) * gridSize,
            direction: Math.floor(Math.random() * 4),
            segments: [],
            color: colors[Math.floor(Math.random() * colors.length)],
            growing: true,
            turnChance: 0.1 + Math.random() * 0.1
          });
        }
      }

      // Draw all pipes
      pipesRef.current.forEach(pipe => {
        ctx.strokeStyle = pipe.color;
        ctx.lineWidth = gridSize * 0.6;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Draw segments with glow effect
        ctx.shadowColor = pipe.color;
        ctx.shadowBlur = 10;
        
        ctx.beginPath();
        pipe.segments.forEach((segment, index) => {
          if (index === 0) {
            ctx.moveTo(segment.x + gridSize/2, segment.y + gridSize/2);
          } else {
            ctx.lineTo(segment.x + gridSize/2, segment.y + gridSize/2);
          }
        });
        
        // Include current position if still growing
        if (pipe.growing) {
          ctx.lineTo(pipe.x + gridSize/2, pipe.y + gridSize/2);
        }
        
        ctx.stroke();
        
        // Reset shadow
        ctx.shadowBlur = 0;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 cursor-none z-[9999]"
      onClick={onActivity}
      onMouseMove={onActivity}
      onKeyDown={onActivity}
    >
      <canvas 
        ref={canvasRef}
        className="w-full h-full"
      />
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white text-lg opacity-60">
        Connecting the digital pathways... Move mouse or press any key to exit
      </div>
    </div>
  );
};